import React, { useEffect, useState } from "react";
import { apiRequest } from "../../../../service/apiRequest";
import {
  Breadcrumb,
  Spin,
  Tag,
  Input,
  Button,
  InputNumber,
  Select,
  message,
  Modal,
  Form,
  DatePicker,
} from "antd";
import {
  HomeOutlined,
  CheckCircleFilled,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";

const { TextArea } = Input;

// ── Department config ──────────────────────────────────────
const DEPARTMENTS = [
  { key: "timber", label: "Timber", icon: "🪵" },
  { key: "machinery", label: "Machinery", icon: "⚙️" },
  { key: "assembly", label: "Assembly", icon: "🔧" },
  { key: "sanding", label: "Sanding", icon: "🚚" },
  { key: "finishing", label: "Finishing", icon: "✨" },
  { key: "packing", label: "Packing", icon: "📫" },
] as const;

type DeptKey = (typeof DEPARTMENTS)[number]["key"];

const STATUS_COLORS: Record<string, string> = {
  pending: "#d9d9d9",
  in_progress: "#1677ff",
  completed: "#52c41a",
  delayed: "#ff4d4f",
};

const STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  in_progress: "In Progress",
  completed: "Completed",
  delayed: "Delayed",
};

// ── Types ──────────────────────────────────────────────────
interface PNRow {
  id?: number;
  pn_number: string;
  qty: number;
  status: "pending" | "done";
}
interface Stage {
  id?: number;
  stage_number: number;
  stage_name: string;
  qty: number;
  status: "pending" | "done";
}

interface Department {
  tpid_id: number | null;
  tpid_department: DeptKey;
  tpid_status: string;
  tpid_qty_received: number;
  tpid_qty_completed: number;
  tpid_expected_date: string | null;
  tpid_notes: string | null;
  pn_rows?: PNRow[]; // assembly only
  stages?: Stage[]; // packed only
}

interface ProductDetail {
  pid_id: number;
  pid_pi_id: number;
  product_name: string;
  total_qty: number;
  ready_qty: number;
  pi_order_no: string;
  pi_date: string;
  pi_lead_time: number;
  departments: Department[];
  packed_stages: any[];
}

// ── Helpers ────────────────────────────────────────────────
const card: React.CSSProperties = {
  background: "#fff",
  borderRadius: 12,
  padding: "18px 20px",
  boxShadow: "0 2px 10px rgba(0,0,0,0.07)",
  border: "1.5px solid #f0f0f0",
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const row = (justify = "space-between"): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  justifyContent: justify as any,
  gap: 8,
});

// ── Component ──────────────────────────────────────────────
const TrackPIProductDetail: React.FC = () => {
  const { pi_id, pid_id } = useParams<{ pi_id: string; pid_id: string }>();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<ProductDetail | null>(null);
  const [depts, setDepts] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  // Edit Modal State
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingDept, setEditingDept] = useState<Department | null>(null);
  const [deptForm] = Form.useForm();

  // ── Fetch ────────────────────────────────────────────────
  const fetchDetail = async () => {
    try {
      setLoading(true);
      const data = await apiRequest<ProductDetail>({
        method: "get",
        url: `/get-track-pi-product-detail/${pid_id}`,
      });
      setDetail(data);

      // Merge departments with pn_rows / stages
      const merged = DEPARTMENTS.map((d) => {
        const existing = data.departments.find(
          (x) => x.tpid_department === d.key,
        );
        const base: Department = existing || {
          tpid_id: null,
          tpid_department: d.key,
          tpid_status: "pending",
          tpid_qty_received: 0,
          tpid_qty_completed: 0,
          tpid_expected_date: null,
          tpid_notes: null,
        };
        if (d.key === "assembly") base.pn_rows = base.pn_rows || [];
        if (d.key === "packing")
          base.stages =
            data.packed_stages?.map((s, i) => ({
              id: s.tpips_id,
              stage_number: s.tpips_stage_number || i + 1,
              stage_name: s.tpips_stage_name || `Stage ${i + 1}`,
              qty: Number(s.tpips_qty),
              status: s.tpips_status,
            })) || [];
        return base;
      });
      setDepts(merged);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pid_id) fetchDetail();
  }, [pid_id]);

  const openEditModal = (dept: Department) => {
    setEditingDept(dept);
    deptForm.setFieldsValue({
      tpid_status: dept.tpid_status || "pending",
      tpid_qty_received: dept.tpid_qty_received || 0,
      tpid_qty_completed: dept.tpid_qty_completed || 0,
      tpid_expected_date: dept.tpid_expected_date ? dayjs(dept.tpid_expected_date) : null,
      tpid_notes: dept.tpid_notes || "",
    });
    setEditModalVisible(true);
  };

  const handleSaveDepartment = async () => {
    if (!editingDept || !detail) return;
    try {
      const values = await deptForm.validateFields();
      setSaving(editingDept.tpid_department);

      const payload = {
        pid_id: detail.pid_id,
        pi_id: detail.pid_pi_id,
        tpid_id: editingDept.tpid_id,
        tpid_department: editingDept.tpid_department,
        tpid_status: values.tpid_status,
        tpid_qty_received: values.tpid_qty_received || 0,
        tpid_qty_completed: values.tpid_qty_completed || 0,
        tpid_expected_date: values.tpid_expected_date
          ? dayjs(values.tpid_expected_date).format("YYYY-MM-DD")
          : null,
        tpid_notes: values.tpid_notes || null,
      };

      await apiRequest({
        method: "post",
        url: "/save-track-pi-department",
        data: payload,
      });

      message.success("Department progress updated!");
      setEditModalVisible(false);
      setEditingDept(null);
      fetchDetail();
    } catch (e) {
      console.error(e);
      message.error("Failed to update department");
    } finally {
      setSaving(null);
    }
  };



  // ── Loading / Not found ───────────────────────────────────
  if (loading)
    return (
      <div style={{ textAlign: "center", padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  if (!detail)
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          textAlign: "center",
          color: "#aaa",
          gap: "16px",
        }}
      >
        <svg
          width="220"
          height="180"
          viewBox="0 0 220 180"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <ellipse
            cx="110"
            cy="150"
            rx="55"
            ry="10"
            fill="#d9d9d9"
            opacity="0.35"
          />

          <g transform="translate(20 20)">
            <rect x="0" y="20" width="18" height="70" rx="8" fill="#1677ff" />
            <rect x="0" y="50" width="50" height="18" rx="8" fill="#1677ff" />
            <rect x="32" y="0" width="18" height="90" rx="8" fill="#1677ff" />
          </g>

          <g transform="translate(85 10)">
            <rect
              x="0"
              y="0"
              width="50"
              height="90"
              rx="25"
              stroke="#13c2c2"
              strokeWidth="16"
            />
          </g>

          <g transform="translate(150 20)">
            <rect x="0" y="20" width="18" height="70" rx="8" fill="#722ed1" />
            <rect x="0" y="50" width="50" height="18" rx="8" fill="#722ed1" />
            <rect x="32" y="0" width="18" height="90" rx="8" fill="#722ed1" />
          </g>

          <circle cx="40" cy="30" r="4" fill="#69b1ff" />
          <circle cx="180" cy="40" r="5" fill="#b37feb" />
          <circle cx="120" cy="20" r="3" fill="#5cdbd3" />
          <circle cx="170" cy="120" r="4" fill="#adc6ff" />
          <circle cx="55" cy="120" r="3" fill="#91caff" />
        </svg>

        <div
          style={{
            fontSize: "20px",
            fontWeight: 500,
            color: "#666",
          }}
        >
          Performa Invoices/Product Not found
        </div>
      </div>
    );

  const deadline = dayjs(detail.pi_date).add(detail.pi_lead_time, "day");
  const daysLeft = deadline.diff(dayjs(), "day");
  const tagColor = daysLeft < 0 ? "red" : daysLeft <= 3 ? "orange" : "green";
  const isDone =
    detail.total_qty > 0 &&
    Number(detail.ready_qty) >= Number(detail.total_qty);
  const percent =
    detail.total_qty > 0
      ? Math.min(
        Math.round(
          (Number(detail.ready_qty) / Number(detail.total_qty)) * 100,
        ),
        100,
      )
      : 0;

  return (
    <div>
      {/* ── Breadcrumb ── */}
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item href="/">
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <span
            onClick={() => navigate("/track-pi")}
            style={{ cursor: "pointer", color: "#1677ff" }}
          >
            Track PIs (Performa Invoices)
          </span>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <span
            onClick={() => navigate(`/track-pi/${pi_id}`)}
            style={{ cursor: "pointer", color: "#1677ff" }}
          >
            PI #{detail.pi_order_no}
          </span>
        </Breadcrumb.Item>
        <Breadcrumb.Item>{detail.product_name}</Breadcrumb.Item>
      </Breadcrumb>

      {/* ── Product Summary Card ── */}
      <div
        // style={{
        //   ...card,
        //   flexDirection: "row",
        //   flexWrap: "wrap",
        //   gap: 24,
        //   marginBottom: 28,
        //   border: `2.5px solid ${isDone ? "#52c41a" : "#e8e8e8"}`,
        // }}
        style={{
          ...card,
          border: `2.5px solid ${isDone ? "#52c41a" : "#e8e8e8"}`,
          borderRadius: 12,
          flexDirection: "row",
          gap: 24,
          marginBottom: 28,
          padding: "16px 24px",
          background: "#fff",
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
          width: "100%",
        }}
      >
        <div>
          <div style={{ fontSize: 11, color: "#999" }}>Product</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>
            {detail.product_name}
          </div>
          <div style={{ fontSize: 12, color: "#aaa" }}>
            PI #{detail.pi_order_no}
          </div>
        </div>

        <div
          style={{ width: 1, background: "#f0f0f0", alignSelf: "stretch" }}
        />

        <div style={{ display: "flex", gap: 24 }}>
          <div>
            <div style={{ fontSize: 11, color: "#999" }}>Total QTY</div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>
              {detail.total_qty}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#999" }}>Ready QTY</div>
            <div
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: isDone ? "#52c41a" : "#1677ff",
              }}
            >
              {detail.ready_qty}
              {isDone && (
                <CheckCircleFilled
                  style={{ marginLeft: 6, color: "#52c41a" }}
                />
              )}
            </div>
          </div>
        </div>

        <div
          style={{ width: 1, background: "#f0f0f0", alignSelf: "stretch" }}
        />

        {/* Progress */}
        <div style={{ minWidth: 160 }}>
          <div style={{ fontSize: 11, color: "#999", marginBottom: 6 }}>
            Progress
          </div>
          <div
            style={{
              background: "#f0f0f0",
              borderRadius: 10,
              height: 8,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${percent}%`,
                height: "100%",
                background: isDone ? "#52c41a" : "#1677ff",
                borderRadius: 10,
                transition: "width 0.4s",
              }}
            />
          </div>
          <div
            style={{
              fontSize: 12,
              color: "#aaa",
              marginTop: 4,
              textAlign: "right",
            }}
          >
            {percent}%
          </div>
        </div>

        <div
          style={{ width: 1, background: "#f0f0f0", alignSelf: "stretch" }}
        />

        <div>
          <div style={{ fontSize: 11, color: "#999" }}>Deadline</div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>
            {deadline.format("DD MMM YYYY")}
          </div>
          <Tag
            color={tagColor}
            style={{ marginTop: 4, borderRadius: 20, fontSize: 11 }}
          >
            {daysLeft < 0
              ? `${Math.abs(daysLeft)}d overdue`
              : daysLeft === 0
                ? "Due today"
                : `${daysLeft}d left`}
          </Tag>
        </div>
      </div>

      {/* ── Department Cards ── */}
      <div
        style={{
          fontSize: 15,
          fontWeight: 600,
          color: "#333",
          marginBottom: 16,
        }}
      >
        Departments
      </div>

      <div
        style={{ display: "flex", flexWrap: "wrap", gap: 20, width: "100%" }}
      >
        {depts.map((dept) => {
          const conf = DEPARTMENTS.find((d) => d.key === dept.tpid_department)!;
          const statusColor = STATUS_COLORS[dept.tpid_status] || "#d9d9d9";
          const isCompleted = dept.tpid_status === "completed";

          return (
            <div
              key={dept.tpid_department}
              style={{
                ...card,
                width: 350,
                border: `2px solid ${statusColor}`,
              }}
            >
              {/* ── Card Header ── */}
              <div style={row()}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 20 }}>{conf.icon}</span>
                  <span style={{ fontWeight: 700, fontSize: 15 }}>
                    {isCompleted ? "Completed" : conf.label}
                  </span>
                </div>
                {isCompleted && (
                  <CheckCircleFilled
                    style={{ color: "#52c41a", fontSize: 18 }}
                  />
                )}
              </div>

              {/* Status */}
              <div style={row()}>
                <span style={{ fontSize: 12, color: "#888" }}>Status</span>
                <Tag color={statusColor} style={{ margin: 0, borderRadius: 12, fontSize: 11, fontWeight: 600 }}>
                  {STATUS_LABELS[dept.tpid_status] || "Pending"}
                </Tag>
              </div>

              {/* Qty Received */}
              <div style={row()}>
                <span style={{ fontSize: 12, color: "#888" }}>
                  {conf.key === 'timber' ? 'Required CFT' : (conf.key === 'machinery' ? 'Total Components' : 'Total Qty')}
                </span>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    fontWeight: 700,
                    fontSize: 14,
                  }}
                >
                  {dept.tpid_qty_received}
                  {dept.tpid_qty_received > detail.total_qty && (
                    <span style={{ fontSize: 10, color: "#ff4d4f" }}>
                      Max {detail.total_qty}
                    </span>
                  )}
                </div>
              </div>

              {/* Qty Completed */}
              <div style={row()}>
                <span style={{ fontSize: 12, color: "#888" }}>
                  {conf.key === 'timber' ? 'Issued CFT' : (conf.key === 'machinery' ? 'Completed Component' : 'Completed Qty')}
                </span>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    fontWeight: 700,
                    fontSize: 14,
                    color: isCompleted ? "#52c41a" : "#1677ff",
                  }}
                >
                  {dept.tpid_qty_completed}
                  {dept.tpid_qty_completed > dept.tpid_qty_received && (
                    <span style={{ fontSize: 10, color: "#ff4d4f" }}>
                      Max {dept.tpid_qty_received}
                    </span>
                  )}
                  {dept.tpid_qty_received > 0 && (
                    <span style={{ fontSize: 10, color: "#aaa" }}>
                      {Math.round(
                        (dept.tpid_qty_completed / dept.tpid_qty_received) *
                        100,
                      )}
                      % done
                    </span>
                  )}
                </div>
              </div>

              {/* Expected Date */}
              <div style={row()}>
                <span style={{ fontSize: 12, color: "#888" }}>
                  Expected Date
                </span>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                >
                  {dept.tpid_expected_date ? dayjs(dept.tpid_expected_date).format("DD MMM YYYY") : "N/A"}
                </div>
              </div>

              {/* Notes */}
              {dept.tpid_notes && (
                <div style={{ fontSize: 12, color: "#666", background: "#f8fafc", padding: "6px 10px", borderRadius: 6, border: "1px solid #f1f5f9" }}>
                  <strong>Notes:</strong> {dept.tpid_notes}
                </div>
              )}


            </div>
          );
        })}
      </div>

    </div>
  );
};

export default TrackPIProductDetail;
